import {
  Component,
  effect,
  inject,
  OnInit,
  signal,
  Signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { EntitlementsCache } from '../../core/models/entitlements.model';
import { Form } from '../../core/models/form.model';
import { EntitlementsService } from '../../core/services/entitlements.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalConfig, ModalService } from '../../core/services/modal.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { EntityListSelectorComponent } from '../../shared/components/entity-list-selector/entity-list-selector.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { UpsellDialogComponent } from '../../shared/components/upsell-dialog/upsell-dialog.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-forms',
  standalone: true,
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    IconComponent,
    ButtonComponent,
    HasPermissionDirective,
    TranslatePipe,
    EntityListSelectorComponent,
  ],
  templateUrl: './forms.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './forms.component.css',
})
export class FormsComponent implements OnInit {
  private formService = inject(FormService);
  private entitlementsService = inject(EntitlementsService);
  private modalService = inject(ModalService);
  private translationService = inject(TranslationService);
  private router = inject(Router);

  // Reactive query signals - assigned in ngOnInit from createQuery
  forms!: Signal<Form[]>;
  loading!: Signal<boolean>;
  error!: Signal<string | null>;
  entitlements!: Signal<EntitlementsCache | null>;

  private logger = inject(LoggerService).getLogger('forms');

  constructor() {
    effect(() => {
      const types = this.forms?.();
      if (types) {
        this.logger.debug('forms loaded:', types.length, types);
      }
    });
  }

  async ngOnInit() {
    // Create reactive query that auto-updates on sync changes
    const query = this.formService.createQuery();
    this.forms = query.data;
    this.loading = query.loading;
    this.error = query.error;

    const entitlementsQuery = this.entitlementsService.createQuery();
    this.entitlements = entitlementsQuery.data;
  }

  /**
   * Gates the "New Form" action against the plan's custom-form hard cap.
   * Save-time re-check lives in FormFormComponent as defense in depth
   * (e.g. a direct navigation to /forms/new).
   */
  createForm() {
    const entitlements = this.entitlements?.();
    // Sample-scenario forms don't count toward the plan limit (see FormService.count).
    const currentCount = (this.forms?.() ?? []).filter((f) => !f.sample_scenario).length;

    if (
      entitlements &&
      this.entitlementsService.limitReached(currentCount, entitlements.custom_form_limit)
    ) {
      this.showFormLimitUpsell(entitlements.plan_name);
      return;
    }

    this.router.navigate(['/forms/new']);
  }

  private async showFormLimitUpsell(planName: string): Promise<void> {
    const closeSignal = signal<{ action: string } | null>(null);

    const config: ModalConfig = {
      component: UpsellDialogComponent,
      componentInputs: { limitType: 'customForm', planName },
      title: this.translationService.translate('upsellDialog.title'),
      size: 'small',
      closeSignal,
      actionButtons: [
        {
          id: 'cancel',
          label: this.translationService.translate('upsellDialog.cancel'),
          variant: 'text',
          onClick: async () => closeSignal.set({ action: 'cancel' }),
        },
        {
          id: 'upgrade',
          label: this.translationService.translate('upsellDialog.upgradePlan'),
          variant: 'primary',
          onClick: async () => closeSignal.set({ action: 'upgrade' }),
        },
      ],
    };

    const result = await this.modalService.openModal(config);
    if (result?.action === 'upgrade') {
      this.router.navigate(['/settings/billing']);
    }
  }

  editForm(id: string | undefined) {
    if (id) {
      this.router.navigate(['/forms', id]);
    }
  }

  /**
   * Handle edit event from entity list selector
   */
  handleEdit(type: Form): void {
    this.editForm(type.id);
  }
}
